import React from 'react';
import { createPortal } from 'react-dom';
import { GripVertical, MoveHorizontal, Film } from 'lucide-react';
import { colorForSlotId, enumerateDays, formatTickLabel } from '../../../lib/dateAxisRendering';
import { monthlyPeriodStarts } from '../../../lib/monthlyPeriods';
import { useCreativePreviewUrl } from '../shared/useCreativePreviewUrl';
import type { RotationSegment } from '../shared/rotationSegments';

// CTV-476: the canonical scheduler chart. Used in:
//
//   • Wizard Step 3 (interactive) — drag bar edges to resize, drag the body
//     to shift the window, type into per-segment share inputs on each bar.
//   • Edit Creatives + Extend Creative Review — same interactive surface.
//   • Advertiser campaign detail (read-only) — same visualization with no
//     drag handles, plain text shares.
//
// Each gantt bar segments at overlap boundaries. Solo slices show 100% as
// plain text (non-editable). Overlap slices host an inline numeric input
// (or, when too narrow, a `•` button that opens a popover editor) so all
// share editing happens directly on the chart.

export interface ChartSlot {
  slotId: string;
  filename: string;
  startDate: string;
  endDate: string;
  // Optional storage path so the chart can render a small video thumbnail
  // in the left gutter for each row. When missing (or `slug` isn't supplied)
  // the chart falls back to a film-icon placeholder.
  storagePath?: string | null;
  // CTV-606 (ported in CTV-611): status drives per-bar visual treatment on
  // the advertiser detail Gantt. `retired` renders faded (already-ran
  // history); `pending_review` shows an "Awaiting review" chip. Defaults to
  // 'active' for surfaces that don't supply a status (wizard, editor).
  status?: 'active' | 'pending_review' | 'retired';
}

interface CreativeScheduleChartProps {
  slots: ChartSlot[];
  campaignStartDate: string;
  campaignEndDate: string;
  accentColor: string;
  colors?: string[];
  readOnly?: boolean;
  // Station slug — passed through to /creatives/preview-url so the per-row
  // thumbnail can resolve a short-lived signed view URL.
  slug?: string;
  onSlotChange?: (slotId: string, patch: { startDate?: string; endDate?: string }) => void;
  segments?: RotationSegment[];
  // Inline share edit: user typed a new share for one slot in one rotation
  // segment. Parent routes through applySegmentShareEditWithRecency.
  onSegmentShareChange?: (slotId: string, segmentKey: string, nextShare: number) => void;
}

const BAR_HEIGHT = 20;
const ROW_VERTICAL_GAP = 22;
const LABEL_OFFSET = 18;
const EDGE_HANDLE_PX = 8;
// Thumbnails span the full row vertical (label + bar), aligned top-to-bottom.
const THUMB_HEIGHT_PX = LABEL_OFFSET + BAR_HEIGHT;
const THUMB_WIDTH_PX = 64;
const THUMB_GUTTER_PX = 70;
// Inline share input is sized to fit "100%" — slices wider than this gate
// host the input; narrower slices fall back to a `•` popover trigger.
const INPUT_WIDTH_PX = 40;
const INPUT_MIN_PX = INPUT_WIDTH_PX + 4;

type DragKind = 'start' | 'end' | 'body';
interface DragState {
  slotId: string;
  kind: DragKind;
  originalStart: string;
  originalEnd: string;
  pointerStartX: number;
  initialLeftPx: number;
  initialWidthPx: number;
  windowStartIdx: 0;
  windowEndIdx: number;
  startIdx: number;
  endIdx: number;
}

interface DragTooltip {
  date: string;
  x: number;
  y: number;
}

interface PopoverState {
  slotId: string;
  segmentKey: string;
  share: number;
  anchorRect: { top: number; left: number; width: number; height: number };
}

// Read-only detail popover that opens on bar/segment hover. Surfaces the
// creative filename, the hovered segment's date range, and the segment
// share %. Thumbnails are intentionally omitted — the chart's left gutter
// already shows one per row. Editor mode is also excluded — it already
// shows filename in the row label, dates via the drag tooltip, and share
// via the inline input.
interface HoverPopoverState {
  slotId: string;
  segmentKey: string;
  share: number;
  startDate: string;
  endDate: string;
  filename: string;
  anchorRect: { top: number; left: number; width: number; height: number };
}

const HOVER_OPEN_DELAY_MS = 200;

function clampIdx(idx: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, idx));
}

function segmentKey(seg: { startDate: string; endDate: string }): string {
  return `${seg.startDate}..${seg.endDate}`;
}

const CreativeScheduleChart: React.FC<CreativeScheduleChartProps> = ({
  slots,
  campaignStartDate,
  campaignEndDate,
  accentColor,
  colors,
  readOnly = false,
  slug,
  onSlotChange,
  segments,
  onSegmentShareChange,
}) => {
  const days = React.useMemo(
    () => enumerateDays(campaignStartDate, campaignEndDate),
    [campaignStartDate, campaignEndDate],
  );
  const totalDays = days.length;
  const dayIndex = React.useMemo(() => {
    const m = new Map<string, number>();
    days.forEach((d, i) => m.set(d, i));
    return m;
  }, [days]);

  const tickDates = React.useMemo(() => {
    const anniversaries = monthlyPeriodStarts(campaignStartDate, campaignEndDate);
    const all = [...anniversaries];
    if (campaignEndDate && all[all.length - 1] !== campaignEndDate) {
      all.push(campaignEndDate);
    }
    return all;
  }, [campaignStartDate, campaignEndDate]);

  const chartRef = React.useRef<HTMLDivElement | null>(null);
  const [chartWidthPx, setChartWidthPx] = React.useState(0);
  React.useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    setChartWidthPx(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? el.clientWidth;
      setChartWidthPx(next);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pxPerDay = totalDays > 0 && chartWidthPx > 0 ? chartWidthPx / totalDays : 0;

  const barRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const registerBar = React.useCallback((slotId: string, el: HTMLDivElement | null) => {
    if (el) barRefs.current.set(slotId, el);
    else barRefs.current.delete(slotId);
  }, []);

  const [drag, setDrag] = React.useState<DragState | null>(null);
  const [dragTooltip, setDragTooltip] = React.useState<DragTooltip | null>(null);
  const [popover, setPopover] = React.useState<PopoverState | null>(null);
  const [hover, setHover] = React.useState<HoverPopoverState | null>(null);
  const hoverTimerRef = React.useRef<number | null>(null);

  const closeHover = React.useCallback(() => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHover(null);
  }, []);

  const openHover = React.useCallback(
    (
      e: React.PointerEvent<HTMLDivElement>,
      slot: ChartSlot,
      segment: RotationSegment,
      share: number,
      rowIdx: number,
    ) => {
      if (!readOnly) return;
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const rect = e.currentTarget.getBoundingClientRect();
      const anchor = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      };
      const next: HoverPopoverState = {
        slotId: slot.slotId,
        segmentKey: segmentKey(segment),
        share,
        startDate: segment.startDate,
        endDate: segment.endDate,
        filename: slot.filename || `Commercial ${rowIdx + 1}`,
        anchorRect: anchor,
      };
      if (hoverTimerRef.current != null) {
        window.clearTimeout(hoverTimerRef.current);
      }
      hoverTimerRef.current = window.setTimeout(() => {
        hoverTimerRef.current = null;
        setHover(next);
      }, HOVER_OPEN_DELAY_MS);
    },
    [readOnly],
  );

  // Dismiss the hover popover on scroll — `position: fixed` means a page
  // scroll otherwise detaches the popover from its anchor bar.
  React.useEffect(() => {
    if (!hover) return;
    const onScroll = () => closeHover();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
  }, [hover, closeHover]);

  // Clear any pending open timer on unmount.
  React.useEffect(() => {
    return () => {
      if (hoverTimerRef.current != null) window.clearTimeout(hoverTimerRef.current);
    };
  }, []);
  // CTV-506: index of a snap target a live drag is currently snapping to.
  // Drives the vertical guideline that confirms the snap visually.
  const [snapGuideIdx, setSnapGuideIdx] = React.useState<number | null>(null);

  const chartHeight = slots.length * (BAR_HEIGHT + ROW_VERTICAL_GAP);

  const dateForIdx = (idx: number): string => days[clampIdx(idx, 0, totalDays - 1)] ?? campaignStartDate;

  // For each slot, the rotation segments it's eligible in (with its share).
  const sliceForSlot = React.useCallback(
    (slotId: string): Array<{ segment: RotationSegment; share: number }> => {
      if (!segments) return [];
      const out: Array<{ segment: RotationSegment; share: number }> = [];
      for (const seg of segments) {
        const e = seg.eligible.find((x) => x.creativeId === slotId);
        if (!e) continue;
        out.push({ segment: seg, share: e.share });
      }
      return out;
    },
    [segments],
  );

  const onPointerDownEdge = (e: React.PointerEvent<HTMLDivElement>, slot: ChartSlot, kind: DragKind) => {
    if (readOnly || !onSlotChange) return;
    if (totalDays === 0 || pxPerDay === 0) return;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    // CTV-506: stop the browser from starting a text-selection gesture while
    // the buyer drags a bar. Pair with the select-none class on the chart
    // container while `drag != null`.
    e.preventDefault();

    const startIdx = dayIndex.get(slot.startDate) ?? 0;
    const endIdx = dayIndex.get(slot.endDate) ?? Math.max(0, totalDays - 1);
    const initialLeftPx = startIdx * pxPerDay;
    const initialWidthPx = Math.max(pxPerDay, (endIdx - startIdx + 1) * pxPerDay);

    setDrag({
      slotId: slot.slotId,
      kind,
      originalStart: slot.startDate,
      originalEnd: slot.endDate,
      pointerStartX: e.clientX,
      initialLeftPx,
      initialWidthPx,
      windowStartIdx: 0,
      windowEndIdx: totalDays - 1,
      startIdx,
      endIdx,
    });
  };

  // CTV-506: snap a candidate day index to the nearest sibling-derived snap
  // target within ±max(8px, 1 day). The targets per drag kind:
  //   • 'start' drag → siblingStartIdx (align starts) and siblingEndIdx + 1
  //     (start the day after a sibling ends — contiguous tiling).
  //   • 'end' drag → siblingEndIdx (align ends) and siblingStartIdx − 1.
  //   • 'body' drag → both sets, applied to whichever edge is leading the
  //     drag (sign of dxPx).
  // Returns the snapped idx and the target idx for the guideline; or
  // { idx: candidate, target: null } when nothing snapped.
  const snapToNeighbor = (
    activeSlotId: string,
    candidateIdx: number,
    kind: 'start' | 'end',
  ): { idx: number; target: number | null } => {
    if (pxPerDay === 0) return { idx: candidateIdx, target: null };
    const distanceDays = Math.max(8 / pxPerDay, 1);
    let bestTarget: number | null = null;
    let bestDelta = distanceDays + 1;
    for (const sibling of slots) {
      if (sibling.slotId === activeSlotId) continue;
      const sStart = dayIndex.get(sibling.startDate);
      const sEnd = dayIndex.get(sibling.endDate);
      if (sStart == null || sEnd == null) continue;
      const targets =
        kind === 'start'
          ? [sStart, sEnd + 1]
          : [sEnd, sStart - 1];
      for (const t of targets) {
        if (t < 0 || t > totalDays - 1) continue;
        const delta = Math.abs(candidateIdx - t);
        if (delta <= distanceDays && delta < bestDelta) {
          bestDelta = delta;
          bestTarget = t;
        }
      }
    }
    if (bestTarget == null) return { idx: candidateIdx, target: null };
    return { idx: bestTarget, target: bestTarget };
  };

  const computeDragIdxs = (
    dragState: DragState,
    dxPx: number,
    altKey: boolean,
  ): { nextStartIdx: number; nextEndIdx: number; snapTargetIdx: number | null } => {
    const dayDelta = Math.round(dxPx / pxPerDay);
    let nextStartIdx = dragState.startIdx;
    let nextEndIdx = dragState.endIdx;
    let snapTargetIdx: number | null = null;
    if (dragState.kind === 'start') {
      let candidate = clampIdx(dragState.startIdx + dayDelta, 0, dragState.endIdx);
      if (!altKey) {
        const snapped = snapToNeighbor(dragState.slotId, candidate, 'start');
        candidate = clampIdx(snapped.idx, 0, dragState.endIdx);
        snapTargetIdx = snapped.target;
      }
      nextStartIdx = candidate;
    } else if (dragState.kind === 'end') {
      let candidate = clampIdx(dragState.endIdx + dayDelta, dragState.startIdx, totalDays - 1);
      if (!altKey) {
        const snapped = snapToNeighbor(dragState.slotId, candidate, 'end');
        candidate = clampIdx(snapped.idx, dragState.startIdx, totalDays - 1);
        snapTargetIdx = snapped.target;
      }
      nextEndIdx = candidate;
    } else {
      const span = dragState.endIdx - dragState.startIdx;
      const maxStart = totalDays - 1 - span;
      let candidateStart = clampIdx(dragState.startIdx + dayDelta, 0, Math.max(0, maxStart));
      if (!altKey) {
        // For body drags, snap whichever edge is leading the move.
        const leadingKind: 'start' | 'end' = dxPx >= 0 ? 'end' : 'start';
        const candidateLeadingIdx =
          leadingKind === 'end' ? candidateStart + span : candidateStart;
        const snapped = snapToNeighbor(dragState.slotId, candidateLeadingIdx, leadingKind);
        if (snapped.target != null) {
          candidateStart = clampIdx(
            leadingKind === 'end' ? snapped.idx - span : snapped.idx,
            0,
            Math.max(0, maxStart),
          );
          snapTargetIdx = snapped.target;
        }
      }
      nextStartIdx = candidateStart;
      nextEndIdx = candidateStart + span;
    }
    return { nextStartIdx, nextEndIdx, snapTargetIdx };
  };

  const onPointerMoveDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag || pxPerDay === 0) return;
    const dxPx = e.clientX - drag.pointerStartX;
    const { nextStartIdx, nextEndIdx, snapTargetIdx } = computeDragIdxs(drag, dxPx, e.altKey);

    const el = barRefs.current.get(drag.slotId);
    if (el) {
      const leftPct = (nextStartIdx / totalDays) * 100;
      const widthPct = ((nextEndIdx - nextStartIdx + 1) / totalDays) * 100;
      el.style.left = `${leftPct}%`;
      el.style.width = `${widthPct}%`;
    }

    const anchorIdx =
      drag.kind === 'start'
        ? nextStartIdx
        : drag.kind === 'end'
          ? nextEndIdx
          : Math.round((nextStartIdx + nextEndIdx) / 2);
    const anchorPx = anchorIdx * pxPerDay + (drag.kind === 'end' ? pxPerDay : 0);
    const rowIdx = slots.findIndex((s) => s.slotId === drag.slotId);
    const rowYTop = rowIdx * (BAR_HEIGHT + ROW_VERTICAL_GAP) + LABEL_OFFSET;
    setDragTooltip({
      date: dateForIdx(drag.kind === 'end' ? nextEndIdx : nextStartIdx),
      x: anchorPx,
      y: rowYTop - 6,
    });
    setSnapGuideIdx(snapTargetIdx);
  };

  const onPointerUpDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag || pxPerDay === 0) {
      setDrag(null);
      setDragTooltip(null);
      setSnapGuideIdx(null);
      return;
    }
    e.currentTarget.releasePointerCapture(e.pointerId);
    const dxPx = e.clientX - drag.pointerStartX;
    const { nextStartIdx, nextEndIdx } = computeDragIdxs(drag, dxPx, e.altKey);
    const nextStart = dateForIdx(nextStartIdx);
    const nextEnd = dateForIdx(nextEndIdx);
    if (nextStart !== drag.originalStart || nextEnd !== drag.originalEnd) {
      onSlotChange?.(drag.slotId, { startDate: nextStart, endDate: nextEnd });
    } else {
      const el = barRefs.current.get(drag.slotId);
      if (el) {
        el.style.left = `${(drag.startIdx / totalDays) * 100}%`;
        el.style.width = `${((drag.endIdx - drag.startIdx + 1) / totalDays) * 100}%`;
      }
    }
    setDrag(null);
    setDragTooltip(null);
    setSnapGuideIdx(null);
  };

  const positionFor = (slot: ChartSlot) => {
    if (totalDays === 0) return { left: 0, width: 100 };
    const startIdx = dayIndex.get(slot.startDate) ?? 0;
    const endIdx = dayIndex.get(slot.endDate) ?? Math.max(0, totalDays - 1);
    const left = (startIdx / totalDays) * 100;
    const width = ((endIdx - startIdx + 1) / totalDays) * 100;
    return { left, width };
  };

  if (totalDays === 0) return null;

  return (
    <div className="space-y-1">
      <div className="flex w-full">
        {/* Thumbnail gutter — one preview per row, vertically aligned with
            the row's bar block. */}
        <div className="shrink-0 relative" style={{ width: THUMB_GUTTER_PX, height: chartHeight }}>
          {slots.map((slot, rowIdx) => {
            const rowTop = rowIdx * (BAR_HEIGHT + ROW_VERTICAL_GAP);
            return (
              <div
                key={`thumb-${slot.slotId}`}
                className="absolute"
                style={{
                  top: rowTop,
                  left: 2,
                  width: THUMB_WIDTH_PX,
                  height: THUMB_HEIGHT_PX,
                }}
              >
                <SlotThumbnail slug={slug} storagePath={slot.storagePath ?? null} />
              </div>
            );
          })}
        </div>

        <div
          ref={chartRef}
          className={`relative flex-1 ${drag ? 'select-none' : ''}`}
          style={{ height: chartHeight, touchAction: 'pan-y' }}
        >
          {tickDates.map((d) => {
            const idx = dayIndex.get(d);
            if (idx == null) return null;
            const pct = d === campaignEndDate ? 100 : (idx / totalDays) * 100;
            return (
              <div
                key={`tick-${d}`}
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: `${pct}%`,
                  borderLeft: '1px dotted rgb(203, 213, 225)',
                  opacity: 0.9,
                }}
              />
            );
          })}

          {slots.map((slot, rowIdx) => {
            const { left, width } = positionFor(slot);
            const color = colors?.[rowIdx] ?? colorForSlotId(slot.slotId);
            const rowTop = rowIdx * (BAR_HEIGHT + ROW_VERTICAL_GAP);
            const barTop = rowTop + LABEL_OFFSET;
            const isDraggingThis = drag?.slotId === slot.slotId;
            const slices = sliceForSlot(slot.slotId);
            const slotStartIdx = dayIndex.get(slot.startDate) ?? 0;
            const slotEndIdx = dayIndex.get(slot.endDate) ?? Math.max(0, totalDays - 1);
            const slotSpanDays = Math.max(1, slotEndIdx - slotStartIdx + 1);
            // CTV-606 (G7): retired bars render at 50% opacity so the delivery
            // history reads as receded compared to still-live bars. Overrides
            // the drag/hover states below where applicable.
            const isRetired = slot.status === 'retired';
            const isPending = slot.status === 'pending_review';
            const baseOpacity = isRetired ? 0.5 : 0.92;
            return (
              <React.Fragment key={slot.slotId}>
                <div
                  className="absolute left-0 right-0 flex items-center gap-1.5 text-[10px] tracking-tight text-slate-500 pointer-events-none"
                  style={{ top: rowTop, height: LABEL_OFFSET - 4 }}
                >
                  <span className="truncate">{slot.filename || `Commercial ${rowIdx + 1}`}</span>
                  {/* CTV-606 (G7): retired history reads as past tense. */}
                  {isRetired && (
                    <span className="text-slate-400 italic shrink-0">· Ran</span>
                  )}
                  {/* CTV-606 (G8): pending_review bars carry an inline
                      "Awaiting review" chip so buyers reading the Gantt see
                      the review state without hovering. */}
                  {isPending && (
                    <span className="uppercase tracking-[0.06em] text-[9px] font-semibold px-1.5 py-[1px] rounded-full bg-amber-100 text-amber-800 shrink-0">
                      Awaiting review
                    </span>
                  )}
                </div>
                <div
                  ref={(el) => registerBar(slot.slotId, el)}
                  className="absolute rounded-md overflow-hidden"
                  style={{
                    top: barTop,
                    height: BAR_HEIGHT,
                    left: `${left}%`,
                    width: `${width}%`,
                    backgroundColor: color,
                    opacity: isDraggingThis ? 1 : baseOpacity,
                    cursor: readOnly ? 'default' : 'grab',
                    // CTV-606 (G8): dashed 2px outline on pending bars so the
                    // "under review" state is visible from the Gantt itself.
                    // Retired bars borrow the same treatment with a muted
                    // slate stroke to keep them visually recessive.
                    outline: isDraggingThis
                      ? `2px solid ${accentColor}`
                      : isPending
                        ? '2px dashed rgba(217,119,6,0.7)'
                        : isRetired
                          ? '1px dashed rgba(100,116,139,0.55)'
                          : 'none',
                    outlineOffset: '-1px',
                    touchAction: 'none',
                  }}
                  onPointerMove={(e) => {
                    if (drag) onPointerMoveDrag(e);
                  }}
                  onPointerDown={(e) => {
                    if (readOnly || !onSlotChange) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const localX = e.clientX - rect.left;
                    if (localX <= EDGE_HANDLE_PX) {
                      onPointerDownEdge(e, slot, 'start');
                    } else if (localX >= rect.width - EDGE_HANDLE_PX) {
                      onPointerDownEdge(e, slot, 'end');
                    } else {
                      onPointerDownEdge(e, slot, 'body');
                    }
                  }}
                  onPointerUp={onPointerUpDrag}
                  onPointerCancel={onPointerUpDrag}
                  role={readOnly ? 'img' : 'slider'}
                  aria-label={`${slot.filename || `Commercial ${rowIdx + 1}`} — ${slot.startDate} to ${slot.endDate}`}
                >
                  {!readOnly && (
                    <>
                      {/* Edge resize affordance — rendered after slices so it
                          sits on top, and uses pointer-events:none on the
                          grip icon so pointerdowns reach the bar's drag
                          dispatcher. */}
                      <div
                        className="absolute top-0 bottom-0 left-0 flex items-center justify-center pointer-events-none"
                        style={{ width: EDGE_HANDLE_PX }}
                      >
                        <GripVertical className="w-2.5 h-3 text-white/70" />
                      </div>
                      <div
                        className="absolute top-0 bottom-0 left-0"
                        style={{ width: EDGE_HANDLE_PX, cursor: 'ew-resize' }}
                      />
                      <div
                        className="absolute top-0 bottom-0 right-0 flex items-center justify-center pointer-events-none"
                        style={{ width: EDGE_HANDLE_PX }}
                      >
                        <GripVertical className="w-2.5 h-3 text-white/70" />
                      </div>
                      <div
                        className="absolute top-0 bottom-0 right-0"
                        style={{ width: EDGE_HANDLE_PX, cursor: 'ew-resize' }}
                      />
                    </>
                  )}
                  {slices.length > 0 &&
                    slices.map(({ segment, share }) => {
                      const sStart = dayIndex.get(segment.startDate) ?? slotStartIdx;
                      const sEnd = dayIndex.get(segment.endDate) ?? slotEndIdx;
                      const leftPct = ((sStart - slotStartIdx) / slotSpanDays) * 100;
                      const widthPct = ((sEnd - sStart + 1) / slotSpanDays) * 100;
                      const slicePxWidth = (sEnd - sStart + 1) * pxPerDay;
                      const isOverlap = segment.eligible.length >= 2;
                      const key = segmentKey(segment);
                      const showDivider = sEnd < slotEndIdx;
                      // Solo slice — inert label, no input. In read-only mode
                      // the slice captures hover so the detail popover can
                      // surface filename / dates / share for this segment.
                      if (!isOverlap) {
                        return (
                          <div
                            key={`${slot.slotId}-${key}`}
                            className="absolute top-0 bottom-0 flex items-center justify-center text-[11px] font-semibold text-white whitespace-nowrap"
                            style={{
                              left: `${leftPct}%`,
                              width: `${widthPct}%`,
                              borderRight: showDivider ? '1px solid rgba(255,255,255,0.45)' : 'none',
                              pointerEvents: readOnly ? 'auto' : 'none',
                            }}
                            onPointerEnter={
                              readOnly
                                ? (e) => openHover(e, slot, segment, share, rowIdx)
                                : undefined
                            }
                            onPointerLeave={readOnly ? closeHover : undefined}
                          >
                            {widthPct >= 8 ? `${share}%` : ''}
                          </div>
                        );
                      }
                      // Overlap slice — hosts the inline editor (or a `•`
                      // popover trigger when too narrow). The slice background
                      // does NOT stop pointer events so clicking it still
                      // engages the bar's body/edge drag; only the input and
                      // dot button stop propagation when interacted with.
                      //
                      // CTV-506: when the slice abuts the bar's left or right
                      // edge, the editor/dot is inset by EDGE_HANDLE_PX so the
                      // bar's edge-drag zone stays reachable. Without this,
                      // an overlap segment landing on the bar end covers the
                      // edge handle and the buyer can't pull the bars apart.
                      const abutsStart = sStart === slotStartIdx && !readOnly && !!onSlotChange;
                      const abutsEnd = sEnd === slotEndIdx && !readOnly && !!onSlotChange;
                      return (
                        <div
                          key={`${slot.slotId}-${key}`}
                          className="absolute top-0 bottom-0 pointer-events-none"
                          style={{
                            left: `${leftPct}%`,
                            width: `${widthPct}%`,
                            borderRight: showDivider ? '1px solid rgba(255,255,255,0.55)' : 'none',
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            paddingLeft: abutsStart ? EDGE_HANDLE_PX : 0,
                            paddingRight: abutsEnd ? EDGE_HANDLE_PX : 0,
                          }}
                        >
                          <div className="absolute inset-0" style={{
                            left: abutsStart ? EDGE_HANDLE_PX : 0,
                            right: abutsEnd ? EDGE_HANDLE_PX : 0,
                          }}>
                            {readOnly || !onSegmentShareChange ? (
                              <div
                                className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-white whitespace-nowrap"
                                style={{ pointerEvents: readOnly ? 'auto' : 'none' }}
                                onPointerEnter={
                                  readOnly
                                    ? (e) => openHover(e, slot, segment, share, rowIdx)
                                    : undefined
                                }
                                onPointerLeave={readOnly ? closeHover : undefined}
                              >
                                {widthPct >= 8 ? `${share}%` : ''}
                              </div>
                            ) : slicePxWidth >= INPUT_MIN_PX ? (
                              <InlineShareInput
                                share={share}
                                accentColor={accentColor}
                                onCommit={(next) => onSegmentShareChange(slot.slotId, key, next)}
                              />
                            ) : (
                              <NarrowSliceDot
                                share={share}
                                accentColor={accentColor}
                                onOpen={(rect) =>
                                  setPopover({ slotId: slot.slotId, segmentKey: key, share, anchorRect: rect })
                                }
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </React.Fragment>
            );
          })}

          {drag && snapGuideIdx != null && pxPerDay > 0 && (
            <div
              aria-hidden
              className="absolute z-20 pointer-events-none"
              style={{
                left: snapGuideIdx * pxPerDay,
                top: 0,
                bottom: 0,
                borderLeft: `1px solid ${accentColor}`,
                opacity: 0.85,
              }}
            />
          )}
          {drag && dragTooltip && (
            <div
              className="absolute z-30 pointer-events-none text-white text-[11px] font-semibold rounded-md px-2 py-1 shadow-lg whitespace-nowrap"
              style={{
                left: dragTooltip.x,
                top: Math.max(0, dragTooltip.y - 28),
                transform: 'translateX(-50%)',
                background: snapGuideIdx != null ? accentColor : 'rgb(15, 23, 42)',
              }}
            >
              {dragTooltip.date}
            </div>
          )}
        </div>
      </div>

      <div className="flex w-full">
        <div className="shrink-0" style={{ width: THUMB_GUTTER_PX }} />
        <div className="relative h-4 border-t border-slate-300 flex-1">
          {tickDates.map((d) => {
            const idx = dayIndex.get(d);
            if (idx == null) return null;
            const pct = d === campaignEndDate ? 100 : (idx / totalDays) * 100;
            return (
              <div
                key={`axis-${d}`}
                className="absolute transform -translate-x-1/2"
                style={{ left: `${pct}%`, top: 0 }}
              >
                <div className="w-px h-1.5 bg-slate-400 mx-auto" />
                <div
                  className="text-[9px] text-slate-500 whitespace-nowrap"
                  style={{ marginTop: '1px' }}
                >
                  {formatTickLabel(d, campaignStartDate, campaignEndDate)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!readOnly && (
        <div className="mt-2 flex items-center gap-1.5 text-[12px] text-slate-500">
          <MoveHorizontal className="w-3.5 h-3.5 shrink-0" />
          <span>Drag bar edges to adjust dates · drag a bar to shift the window · click a weight to change its value</span>
        </div>
      )}

      {popover && (
        <NarrowSlicePopover
          anchorRect={popover.anchorRect}
          share={popover.share}
          accentColor={accentColor}
          onCommit={(next) => {
            onSegmentShareChange?.(popover.slotId, popover.segmentKey, next);
            setPopover(null);
          }}
          onClose={() => setPopover(null)}
        />
      )}

      {hover && <HoverDetailPopover hover={hover} />}
    </div>
  );
};

interface HoverDetailPopoverProps {
  hover: HoverPopoverState;
}

// Read-only bar/segment detail popover. Rendered via portal so it can escape
// the chart's overflow box, with `pointerEvents: 'none'` so it never steals
// focus from adjacent segments — visibility is owned by the anchor's hover
// state. A one-pass useLayoutEffect clamps `left` to keep the popover inside
// the viewport (the only delta from NarrowSlicePopover's positioning math).
const HoverDetailPopover: React.FC<HoverDetailPopoverProps> = ({ hover }) => {
  const popRef = React.useRef<HTMLDivElement | null>(null);
  const [clampedLeft, setClampedLeft] = React.useState<number | null>(null);
  const initialLeft = hover.anchorRect.left + hover.anchorRect.width / 2;

  React.useLayoutEffect(() => {
    const el = popRef.current;
    if (!el) return;
    const margin = 8;
    const halfWidth = el.offsetWidth / 2;
    const minCenter = margin + halfWidth;
    const maxCenter = window.innerWidth - margin - halfWidth;
    if (maxCenter < minCenter) {
      setClampedLeft(window.innerWidth / 2);
      return;
    }
    const clamped = Math.max(minCenter, Math.min(maxCenter, initialLeft));
    setClampedLeft(clamped);
  }, [initialLeft, hover.slotId, hover.segmentKey]);

  return createPortal(
    <div
      ref={popRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: hover.anchorRect.top - 6,
        left: clampedLeft ?? initialLeft,
        transform: 'translate(-50%, -100%)',
        zIndex: 999,
        width: 220,
        pointerEvents: 'none',
      }}
      className="bg-slate-900 text-white rounded-md shadow-xl px-3 py-2"
    >
      <div className="text-[11px] font-semibold truncate" title={hover.filename}>
        {hover.filename}
      </div>
      <div className="text-[10px] text-slate-300">
        {hover.startDate} → {hover.endDate}
      </div>
      <div className="text-[10px] text-slate-300">
        <span className="font-semibold text-white">{hover.share}%</span> share
      </div>
    </div>,
    document.body,
  );
};

const SlotThumbnail: React.FC<{ slug?: string; storagePath?: string | null }> = ({
  slug,
  storagePath,
}) => {
  const { url, posterUrl, loading } = useCreativePreviewUrl(slug, storagePath);
  if (!slug || !storagePath || loading || !url) {
    return (
      <div className="w-full h-full rounded bg-slate-200 flex items-center justify-center">
        <Film className="w-3 h-3 text-slate-400" aria-hidden />
      </div>
    );
  }
  // CTV-508: prefer the server-generated poster (representative non-black
  // frame); fall back to the raw video first frame when there's no poster.
  return posterUrl ? (
    <img
      src={posterUrl}
      alt="Creative thumbnail"
      className="w-full h-full rounded bg-black object-cover"
    />
  ) : (
    <video
      src={url}
      preload="metadata"
      muted
      playsInline
      className="w-full h-full rounded bg-black object-cover"
    />
  );
};

interface InlineShareInputProps {
  share: number;
  accentColor: string;
  onCommit: (next: number) => void;
}

// Inline numeric share editor — sits on top of an overlap slice on the
// gantt bar. Shows `${share}%` at rest; on focus reveals digits-only for
// typing. Underlined on focus, no border at rest.
const InlineShareInput: React.FC<InlineShareInputProps> = ({ share, accentColor, onCommit }) => {
  const [editing, setEditing] = React.useState(false);
  const [text, setText] = React.useState<string>('');

  const commit = () => {
    if (!editing) return;
    setEditing(false);
    if (text === '') return;
    const parsed = Number(text);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed !== share) {
      onCommit(parsed);
    }
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <input
        type="text"
        inputMode="decimal"
        value={editing ? text : `${share}%`}
        onFocus={(e) => {
          setEditing(true);
          setText(String(share));
          e.currentTarget.select();
        }}
        onChange={(e) => setText(e.currentTarget.value.replace(/\D/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setEditing(false);
            e.currentTarget.blur();
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        aria-label="Segment share"
        className="pointer-events-auto cursor-text bg-transparent text-[11px] font-semibold text-white text-center outline-none border-0 border-b border-dashed border-white/60 hover:border-solid hover:border-white hover:bg-black/20 focus:border-solid focus:border-white focus:bg-black/20 rounded-sm px-1 py-0 transition-colors duration-200 ease-out"
        style={{ width: INPUT_WIDTH_PX, caretColor: accentColor }}
      />
    </div>
  );
};

interface NarrowSliceDotProps {
  share: number;
  accentColor: string;
  onOpen: (rect: { top: number; left: number; width: number; height: number }) => void;
}

const NarrowSliceDot: React.FC<NarrowSliceDotProps> = ({ share, accentColor, onOpen }) => {
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const [tipRect, setTipRect] = React.useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const showTip = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setTipRect({ top: r.top, left: r.left, width: r.width });
  };
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onPointerEnter={showTip}
        onPointerLeave={() => setTipRect(null)}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setTipRect(null);
          const el = btnRef.current;
          if (!el) return;
          const rect = el.getBoundingClientRect();
          onOpen({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        }}
        className="pointer-events-auto absolute inset-0 flex items-center justify-center text-white"
        aria-label={`Edit segment share (currently ${share}%)`}
        style={{ background: 'transparent' }}
      >
        <span
          className="text-[14px] leading-none"
          aria-hidden
          style={{ color: 'white', textShadow: `0 0 2px ${accentColor}` }}
        >
          •
        </span>
      </button>
      {tipRect &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              top: tipRect.top - 6,
              left: tipRect.left + tipRect.width / 2,
              transform: 'translate(-50%, -100%)',
              zIndex: 999,
            }}
            className="bg-slate-900 text-white text-[10px] font-medium rounded-md px-2 py-1 shadow-md whitespace-nowrap pointer-events-none"
          >
            <span className="font-semibold">{share}%</span>
            <span className="text-slate-300"> · click to edit</span>
          </div>,
          document.body,
        )}
    </>
  );
};

interface NarrowSlicePopoverProps {
  anchorRect: { top: number; left: number; width: number; height: number };
  share: number;
  accentColor: string;
  onCommit: (next: number) => void;
  onClose: () => void;
}

const NarrowSlicePopover: React.FC<NarrowSlicePopoverProps> = ({
  anchorRect,
  share,
  accentColor,
  onCommit,
  onClose,
}) => {
  const popRef = React.useRef<HTMLDivElement | null>(null);
  const [text, setText] = React.useState<string>(String(share));

  // Keep the input in sync with the segment the popover is anchored to —
  // clicking a different dot updates the `share` prop, which we mirror into
  // the local text state so the user always sees the actual current share.
  React.useEffect(() => {
    setText(String(share));
  }, [share]);

  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const commit = () => {
    if (text === '') {
      onClose();
      return;
    }
    const parsed = Number(text);
    if (Number.isFinite(parsed) && parsed >= 0) onCommit(parsed);
    else onClose();
  };

  return createPortal(
    <div
      ref={popRef}
      role="dialog"
      aria-label="Edit segment share"
      style={{
        position: 'fixed',
        top: anchorRect.top - 6,
        left: anchorRect.left + anchorRect.width / 2,
        transform: 'translate(-50%, -100%)',
        zIndex: 1000,
      }}
      className="bg-white border border-slate-300 rounded-md shadow-lg px-2 py-2 flex items-center gap-1.5"
    >
      <input
        autoFocus
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => setText(e.currentTarget.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') onClose();
        }}
        className="w-16 text-[12px] font-semibold tracking-tight text-slate-900 bg-white border border-slate-300 rounded-md px-2 py-1 text-right focus:outline-none focus:ring-2"
        style={{ '--tw-ring-color': `${accentColor}55` } as React.CSSProperties}
      />
      <span className="text-[12px] text-slate-500">%</span>
      <button
        type="button"
        onClick={commit}
        className="text-[11px] font-semibold uppercase tracking-[0.08em] px-2 py-1 rounded-md text-white"
        style={{ backgroundColor: accentColor }}
      >
        Set
      </button>
    </div>,
    document.body,
  );
};

export default CreativeScheduleChart;
